import {
  ANNOUNCEMENT_BAR_DISPLAY_TYPE,
  ANNOUNCEMENT_BAR_POSITION,
  BANNER_TYPE,
  NAVIGATION_TYPE,
  POPUP_TRIGGER_TYPE,
  SELLER_SEGMENT_TYPE,
  TIME_STATUS,
} from "../core/constants";
import { formatRFC3339, parseMoment } from "../core/utils";

/**
 * Map search filters từ FE sang BE SearchBannersFilters
 */
export function mapSearchFiltersToBackend(filters = {}) {
  const result = {};

  if (filters.types && filters.types.length > 0) {
    result.types = filters.types;
  }
  if (filters.sources && filters.sources.length > 0) {
    result.sources = filters.sources;
  }
  if (filters.screens && filters.screens.length > 0) {
    result.screens = filters.screens;
  }
  if (filters.timeStatus && filters.timeStatus !== TIME_STATUS.ALL) {
    result.timeStatus = filters.timeStatus;
  }
  if (typeof filters.isActive === "boolean") {
    result.isActive = filters.isActive;
  }
  if (filters.timeFilter && filters.timeFilter[0] && filters.timeFilter[1]) {
    result.timeFilter = {
      from: formatRFC3339(filters.timeFilter[0]),
      to: formatRFC3339(filters.timeFilter[1]),
    };
  }
  if (filters.query && filters.query.trim()) {
    result.query = filters.query.trim();
  }

  return result;
}

/**
 * Chuẩn bị CreateBannerInput từ form values
 */
export function mapCreateInputToBackend(values, type = BANNER_TYPE.BANNER) {
  const [start, end] = values.timeRange || [];

  const input = {
    type,
    name: values.name.trim(),
    startTime: formatRFC3339(start),
    endTime: formatRFC3339(end),
    sellerSegmentType: values.sellerSegmentType || SELLER_SEGMENT_TYPE.ALL,
    screens: values.screens || [],
  };

  // Applicable products
  if (values.sellerSegmentType === SELLER_SEGMENT_TYPE.NOT_PURCHASED_PRODUCT) {
    input.applicableProductIDs = values.applicableProductIDs || [];
  }

  // Navigation
  const navType = values.navigationType || NAVIGATION_TYPE.NONE;
  if (navType === NAVIGATION_TYPE.NONE) {
    input.navigation = { type: NAVIGATION_TYPE.NONE };
  } else if (navType === NAVIGATION_TYPE.LANDING_PAGE || navType === NAVIGATION_TYPE.APP_FEATURE) {
    input.navigation = {
      type: navType,
      url: values.navigationURL ? values.navigationURL.trim() : "",
    };
  } else {
    input.navigation = {
      type: navType,
      targetID: values.navigationTargetID || "",
    };
  }

  // Type-specific fields
  if (type === BANNER_TYPE.BANNER) {
    if (values.imageURL) input.imageURL = values.imageURL;
    if (values.squareImageURL) input.squareImageURL = values.squareImageURL;
  } else if (type === BANNER_TYPE.POPUP) {
    input.imageURL = values.imageURL;
    input.popup = {
      triggerType: values.popupTriggerType || POPUP_TRIGGER_TYPE.APP_OPEN_OR_SCREEN_ENTER,
      triggerDelayValue: Number(values.popupTriggerDelayValue || 0),
      triggerDelayUnit: values.popupTriggerDelayUnit || "SECOND",
      displayDurationSeconds:
        values.displayDurationType === "LIMITED" ? Number(values.displayDurationSeconds || 5) : null,
      maxImpressions: Number(values.maxImpressions || 1),
      stopAfterClicks: Number(values.stopAfterClicks || 1),
      frequencyCount: Number(values.frequencyCount || 1),
      frequencyPeriodValue: Number(values.frequencyPeriodValue || 1),
      frequencyPeriod: values.frequencyPeriod || "DAY",
    };
  } else if (type === BANNER_TYPE.ANNOUNCEMENT_BAR) {
    const isImage = values.announcementBarDisplayType === ANNOUNCEMENT_BAR_DISPLAY_TYPE.IMAGE;
    input.announcementBar = {
      position: values.announcementBarPosition || ANNOUNCEMENT_BAR_POSITION.TOP,
      allowClose: Boolean(values.announcementBarAllowClose),
      displayType: values.announcementBarDisplayType || ANNOUNCEMENT_BAR_DISPLAY_TYPE.TEXT,
    };

    if (isImage) {
      input.imageURL = values.imageURL;
    } else {
      input.announcementBar.message = values.announcementBarMessage?.trim() || "";
      input.announcementBar.ctaText = values.announcementBarCtaText?.trim() || null;
      input.announcementBar.iconMode = values.announcementBarIconMode || "NONE";
      if (values.announcementBarIconMode === "PRESET") {
        input.announcementBar.iconPresetCode = values.announcementBarIconPresetCode || null;
      } else if (values.announcementBarIconMode === "UPLOAD") {
        input.announcementBar.iconURL = values.announcementBarIconURL || null;
      }

      input.announcementBar.backgroundMode = values.announcementBarBackgroundMode || "PRESET";
      if (values.announcementBarBackgroundMode === "PRESET") {
        input.announcementBar.colorPresetCode = values.announcementBarColorPresetCode || null;
      } else if (values.announcementBarBackgroundMode === "SOLID") {
        input.announcementBar.backgroundColor = values.announcementBarBackgroundColor || null;
      } else if (values.announcementBarBackgroundMode === "GRADIENT") {
        input.announcementBar.gradientFromColor = values.announcementBarGradientFromColor || null;
        input.announcementBar.gradientToColor = values.announcementBarGradientToColor || null;
        input.announcementBar.gradientAngle = Number(values.announcementBarGradientAngle || 90);
      }

      input.announcementBar.textColor = values.announcementBarTextColor || null;
      input.announcementBar.iconColor = values.announcementBarIconColor || null;
      input.announcementBar.buttonColor = values.announcementBarButtonColor || null;
      input.announcementBar.buttonTextColor = values.announcementBarButtonTextColor || null;
    }
  }

  return input;
}

/**
 * Chuẩn bị UpdateBannerInput từ form values
 */
export function mapUpdateInputToBackend(values, type = BANNER_TYPE.BANNER, source = "MANUAL") {
  // Với banner PROMOTION, BE chỉ nhận imageURL, squareImageURL và screens;
  if (source === "PROMOTION") {
    return {
      imageURL: values.imageURL || null,
      squareImageURL: values.squareImageURL || null,
      screens: values.screens || [],
    };
  }

  // Tương tự createInput nhưng loại bỏ `type` vì UpdateBannerInput trên BE không nhận trường type (type không đổi được)
  const updateInput = { ...mapCreateInputToBackend(values, type) };
  delete updateInput.type;
  return updateInput;
}

/**
 * Chuyển Banner GraphQL response thành Initial Form Values
 */
export function mapBackendDetailToFormValues(banner) {
  if (!banner) return null;

  const start = parseMoment(banner.startTime);
  const end = parseMoment(banner.endTime);

  const values = {
    name: banner.name,
    type: banner.type,
    source: banner.source,
    timeRange: start && end ? [start, end] : null,
    sellerSegmentType: banner.sellerSegmentType || SELLER_SEGMENT_TYPE.ALL,
    applicableProductIDs: banner.applicableProductIDs || [],
    applicableProducts: banner.applicableProducts || [],
    screens: (banner.screens || []).map((s) => s.screen),
    imageURL: banner.imageURL || "",
    squareImageURL: banner.squareImageURL || "",
    navigationType: banner.navigation?.type || NAVIGATION_TYPE.NONE,
    navigationTargetID: banner.navigation?.targetID || null,
    navigationURL: banner.navigation?.url || "",
    navigationTargetName: banner.navigation?.targetName || "",
  };

  // Popup
  if (banner.popup) {
    values.popupTriggerType = banner.popup.triggerType;
    values.popupTriggerDelayValue = banner.popup.triggerDelayValue;
    values.popupTriggerDelayUnit = banner.popup.triggerDelayUnit || "SECOND";
    values.displayDurationType = banner.popup.displayDurationSeconds != null ? "LIMITED" : "UNLIMITED";
    values.displayDurationSeconds = banner.popup.displayDurationSeconds;
    values.maxImpressions = banner.popup.maxImpressions;
    values.stopAfterClicks = banner.popup.stopAfterClicks;
    values.frequencyCount = banner.popup.frequencyCount;
    values.frequencyPeriodValue = banner.popup.frequencyPeriodValue;
    values.frequencyPeriod = banner.popup.frequencyPeriod || "DAY";
  }

  // Announcement Bar
  if (banner.announcementBar) {
    values.announcementBarPosition = banner.announcementBar.position || ANNOUNCEMENT_BAR_POSITION.TOP;
    values.announcementBarAllowClose = Boolean(banner.announcementBar.allowClose);
    values.announcementBarDisplayType = banner.announcementBar.displayType || ANNOUNCEMENT_BAR_DISPLAY_TYPE.TEXT;
    values.announcementBarMessage = banner.announcementBar.message || "";
    values.announcementBarCtaText = banner.announcementBar.ctaText || "";
    values.announcementBarIconMode = banner.announcementBar.iconMode || "NONE";
    values.announcementBarIconPresetCode = banner.announcementBar.iconPresetCode || "";
    values.announcementBarIconURL = banner.announcementBar.iconURL || "";
    values.announcementBarBackgroundMode = banner.announcementBar.backgroundMode || "PRESET";
    values.announcementBarColorPresetCode = banner.announcementBar.colorPresetCode || "";
    values.announcementBarBackgroundColor = banner.announcementBar.backgroundColor || "#00a86b";
    values.announcementBarGradientFromColor = banner.announcementBar.gradientFromColor || "#ff6a00";
    values.announcementBarGradientToColor = banner.announcementBar.gradientToColor || "#ee0979";
    values.announcementBarGradientAngle = banner.announcementBar.gradientAngle ?? 90;
    values.announcementBarTextColor = banner.announcementBar.textColor || "#ffffff";
    values.announcementBarIconColor = banner.announcementBar.iconColor || "#ffffff";
    values.announcementBarButtonColor = banner.announcementBar.buttonColor || "#ffffff";
    values.announcementBarButtonTextColor = banner.announcementBar.buttonTextColor || "#ee0979";
  }

  return values;
}
